import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Mail, Lock, Loader2, ArrowLeft, AtSign } from "lucide-react";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(!searchParams.get("signup"));
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Check for signup param on mount
  useEffect(() => {
    if (searchParams.get("signup") === "true") {
      setIsLogin(false);
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      }, {
        withCredentials: true
      });

      // Store session token as backup
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }

      // Reset per-login session flags (e.g. Kid-Friendly mature-rating warning)
      try { sessionStorage.removeItem("mp_kidsafe_mature_ack"); } catch {}

      toast.success("Welcome back!");
      
      // Force reload to ensure auth state is fresh
      window.location.href = "/dashboard";
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      
      // If account needs verification, offer to resend
      if (message.includes("verify your email")) {
        toast.info("Need a new verification email?", {
          action: {
            label: "Resend",
            onClick: () => handleResendVerification()
          },
          duration: 10000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, {
        email,
        password,
        name,
        username: username.trim() || undefined
      });

      toast.success("Registration successful! Check your email to verify your account.");
      setIsLogin(true);
      setPassword("");
      setConfirmPassword("");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success("Verification email sent! Check your inbox.");
    } catch (error) {
      toast.error("Failed to send verification email");
    }
  };

  const getPasswordStrength = () => {
    if (!password) return null;
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength++;
    
    return strength;
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="Game-o-Rang" className="w-32 h-32 object-contain" />
          </div>
          <p className="text-white/60">Organize your game. Own every detail.</p>
        </div>

        {/* Auth Card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white text-center mb-6">
            {isLogin ? "Sign in to continue" : "Create your account"}
          </h2>

          <form onSubmit={isLogin ? handleLogin : handleRegister} className="space-y-4">
            {/* Name field (register only) */}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white/70">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={!isLogin}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="register-name"
                />
              </div>
            )}

            {/* Username field (register only) */}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="username" className="text-white/70">
                  Username <span className="text-white/30 text-xs">(optional — shown on signature cards)</span>
                </Label>
                <div className="relative">
                  <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="e.g. MrAspirin"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="register-username"
                    maxLength={20}
                  />
                </div>
                <p className="text-xs text-white/30">3-20 characters, letters, numbers, underscores, hyphens</p>
              </div>
            )}

            {/* Email field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="login-email"
                />
              </div>
            </div>

            {/* Password field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">Password</Label>
              {!isLogin && (
                <p className="text-xs text-white/50 mb-1">
                  Must include: 8+ characters, uppercase & lowercase letters, a number, and a special character (!@#$%^&* etc.)
                </p>
              )}
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              {/* Password strength indicator (register only) */}
              {!isLogin && password && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          passwordStrength >= level
                            ? level <= 1 ? "bg-red-500"
                            : level <= 2 ? "bg-yellow-500"
                            : level <= 3 ? "bg-blue-500"
                            : "bg-green-500"
                            : "bg-white/10"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-white/40">
                    {passwordStrength < 4 && (
                      <>
                        Needs: {password.length < 8 && "8+ chars, "}
                        {!/[A-Z]/.test(password) && "uppercase, "}
                        {!/[0-9]/.test(password) && "number, "}
                        {!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) && "special char"}
                      </>
                    )}
                    {passwordStrength === 4 && <span className="text-green-400">Strong password!</span>}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm password (register only) */}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white/70">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={`pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 ${
                      confirmPassword && password !== confirmPassword ? "border-red-500/50" : ""
                    }`}
                    data-testid="register-confirm-password"
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-400">Passwords don't match</p>
                )}
              </div>
            )}

            {/* Forgot password link */}
            {isLogin && (
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-purple-400 hover:text-purple-300"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            )}

            {/* Submit button */}
            <Button
              type="submit"
              disabled={loading || (!isLogin && password !== confirmPassword)}
              className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl"
              data-testid={isLogin ? "login-submit" : "register-submit"}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                isLogin ? "Sign In" : "Create Account"
              )}
            </Button>
          </form>

          {/* Toggle login/register */}
          <div className="mt-6 text-center">
            <p className="text-white/40 text-sm">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setPassword("");
                  setConfirmPassword("");
                  setUsername("");
                }}
                className="ml-2 text-purple-400 hover:text-purple-300 font-medium"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Sign Up" : "Sign In"}
              </button>
            </p>
          </div>

          <p className="text-center text-white/30 text-xs mt-6">
            By signing in, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
}
